import { useMemo } from "react";
import { useTellescopeInfiniteQuery, type UseTellescopeInfiniteQueryOptions } from "..";
import type { TellescopeModelFromName } from "../../mappings";
import type { ParamsForMethod } from "../../types";

const DEFAULT_VITALS_LIMIT = 500;
const UNITS_ALWAYS_COMBINED = ["SBP", "DBP"];

type EnduserObservation = TellescopeModelFromName<"enduser_observations">;

export type CombinedClassification = NonNullable<EnduserObservation["classifications"]>[number] & {
  observationId: string;
};

export type CombinedMeasurement = EnduserObservation["measurement"] & {
  observationId: string;
};

export type CombinedObservation = Omit<EnduserObservation, "measurement" | "classifications"> & {
  measurements: CombinedMeasurement[];
  classifications: CombinedClassification[];
};

/** Filters for querying vitals observations. */
export interface VitalsQueryFilters {
  /** Filter by review status. Omit to include all. */
  reviewed?: "reviewed" | "unreviewed";
  /** Filter by classification values. */
  classifications?: {
    /** Whether observations must match any or all of the specified values. */
    match: "any" | "all";
    /** Classification values to filter by (e.g. "Target", "High", "Critical Low"). */
    values: string[];
  };
  /** Filter to observations from these sources. */
  sources?: string[];
  /** Filter to observations for these enduser IDs. */
  enduserIds?: string[];
  /**
   * Filter to observations for endusers on these care teams.
   * Note: When set, forces use of `filter` instead of `mdbFilter` for the query,
   * and classification filtering falls back to client-side.
   */
  careTeamIds?: string[];
  /** Filter by observation timestamp range. */
  dateRange?: {
    from?: Date;
    to?: Date;
  };
  /** Filter to observations with classifications from these vital configuration IDs. */
  configurationIds?: string[];
  /** Filter to observations with these measurement units (e.g. "SBP", "DBP", "mg/dL"). */
  units?: string[];
  /** Filter by validity status. "valid" excludes entered-in-error observations, "invalid" shows only entered-in-error. Omit to include all. */
  validStatus?: "valid" | "invalid";
  /** Filter by before/after meal status. */
  beforeMeal?: boolean;
  /** Filter by medication status (e.g. "Medication: Before Meds", "Medication: After Meds"). */
  medStatus?: string;
}

export interface UseVitalsQueryWithFiltersArgs {
  /** Filters to apply to the vitals query. */
  filters?: VitalsQueryFilters;
  /**
   * Whether to combine related observations (e.g. SBP/DBP taken at the same time)
   * into single rows with multiple measurements.
   * @default true
   */
  combineRelatedObservations?: boolean;
  /**
   * Number of observations to fetch per page.
   * @default 500
   */
  limit?: number;
}

type CombinedObservationList = CombinedObservation[];

export type UseVitalsQueryOptions<TError = Error, TData = CombinedObservationList> = Omit<
  UseTellescopeInfiniteQueryOptions<"enduser_observations", "getSome", string | undefined, TError, TData>,
  "initialPageParam" | "getNextPageParam" | "paginateRequest" | "select" | "model" | "method" | "request"
> & {
  /** Optional transform applied after filtering and combining. */
  select?: (data: CombinedObservationList) => TData;
};

type MdbFilter = NonNullable<ParamsForMethod<"enduser_observations", "getSome">>["mdbFilter"];
type ApiFilter = NonNullable<ParamsForMethod<"enduser_observations", "getSome">>["filter"] & {
  _careTeam?: string[];
};

/**
 * Builds an mdbFilter for the query. Used when careTeamIds is NOT present,
 * allowing all filters (including classifications) to be server-side.
 */
const buildMdbFilter = (filters: VitalsQueryFilters): MdbFilter | undefined => {
  const mdbFilter: MdbFilter = {};

  if (filters.reviewed === "reviewed") {
    mdbFilter.reviewedAt = { $nin: [null, ""] };
  } else if (filters.reviewed === "unreviewed") {
    mdbFilter.reviewedAt = { $in: [null, ""] };
  }

  if (filters.sources && filters.sources.length > 0) {
    mdbFilter.source = { $in: filters.sources };
  }

  if (filters.enduserIds && filters.enduserIds.length > 0) {
    mdbFilter.enduserId = { $in: filters.enduserIds };
  }

  if (filters.configurationIds && filters.configurationIds.length > 0) {
    mdbFilter["classifications.configurationId"] = { $in: filters.configurationIds };
  }

  if (filters.units && filters.units.length > 0) {
    mdbFilter["measurement.unit"] = { $in: filters.units };
  }

  if (filters.validStatus === "invalid") {
    mdbFilter.status = "entered-in-error";
  } else if (filters.validStatus === "valid") {
    mdbFilter.status = { $ne: "entered-in-error" };
  }

  if (filters.beforeMeal !== undefined) {
    mdbFilter.beforeMeal = filters.beforeMeal;
  }

  if (filters.medStatus) {
    mdbFilter.medStatus = filters.medStatus;
  }

  if (filters.dateRange?.from || filters.dateRange?.to) {
    const timestampFilter: Record<string, Date> = {};
    if (filters.dateRange.from) {
      timestampFilter.$gte = filters.dateRange.from;
    }
    if (filters.dateRange.to) {
      timestampFilter.$lte = filters.dateRange.to;
    }
    mdbFilter.timestamp = timestampFilter;
  }

  // Classifications can go server-side via mdbFilter
  if (filters.classifications && filters.classifications.values.length > 0) {
    if (filters.classifications.match === "any") {
      mdbFilter["classifications.classification"] = { $in: filters.classifications.values };
    } else {
      // "all" — observation must have classifications AND every one must be in the allowed values
      mdbFilter.$and = [
        { classifications: { $exists: true, $ne: [] } },
        {
          classifications: {
            $not: {
              $elemMatch: {
                classification: { $nin: filters.classifications.values },
              },
            },
          },
        },
      ];
    }
  }

  return Object.keys(mdbFilter).length > 0 ? mdbFilter : undefined;
};

/**
 * Builds a filter for the query. Used when careTeamIds IS present,
 * since careTeam filtering only works via `filter`.
 * Classification filtering cannot be included here and must be done client-side.
 */
const buildFilter = (filters: VitalsQueryFilters): ApiFilter | undefined => {
  const filter: ApiFilter = {};

  if (filters.reviewed === "reviewed") {
    filter.reviewedAt = { _exists: true };
  } else if (filters.reviewed === "unreviewed") {
    filter.reviewedAt = { _exists: false };
  }

  if (filters.sources && filters.sources.length > 0) {
    filter.source = { _in: filters.sources };
  }

  if (filters.enduserIds && filters.enduserIds.length > 0) {
    filter.enduserId = { _in: filters.enduserIds };
  }

  if (filters.careTeamIds && filters.careTeamIds.length > 0) {
    filter._careTeam = filters.careTeamIds;
  }

  if (filters.dateRange?.from || filters.dateRange?.to) {
    const dateFilter: Record<string, Date> = {};
    if (filters.dateRange.from) {
      dateFilter._gte = filters.dateRange.from;
    }
    if (filters.dateRange.to) {
      dateFilter._lte = filters.dateRange.to;
    }
    filter.timestamp = dateFilter;
  }

  if (filters.units && filters.units.length > 0) {
    // @ts-expect-error - In reality the filters support nested objects
    filter["measurement.unit"] = { _in: filters.units };
  }

  if (filters.validStatus === "invalid") {
    filter.status = "entered-in-error";
  } else if (filters.validStatus === "valid") {
    filter.status = { _ne: "entered-in-error" };
  }

  if (filters.beforeMeal !== undefined) {
    filter.beforeMeal = filters.beforeMeal;
  }

  if (filters.medStatus) {
    filter.medStatus = filters.medStatus;
  }

  // Note: configurationIds filtering is not supported via `filter` — handled client-side

  return Object.keys(filter).length > 0 ? filter : undefined;
};

const buildClassificationPredicate = (
  classifications: VitalsQueryFilters["classifications"]
): ((obs: EnduserObservation) => boolean) | null => {
  if (!classifications || classifications.values.length === 0) return null;

  const { match, values } = classifications;

  return (obs) => {
    const obsClassifications = obs.classifications ?? [];
    if (match === "any") {
      return obsClassifications.some(({ classification }) => values.includes(classification));
    }
    // "all" — must have classifications AND every one must be in the allowed values
    return (
      obsClassifications.length > 0 && obsClassifications.every(({ classification }) => values.includes(classification))
    );
  };
};

const combineRelatedObservations = (observations: CombinedObservation[]): CombinedObservation[] => {
  return observations.reduce<CombinedObservation[]>((acc, entry) => {
    const previousObservation = acc.pop();

    if (!previousObservation) {
      acc.push(entry);
      return acc;
    }

    const measurement = entry.measurements[0];
    if (!measurement) {
      acc.push(previousObservation);
      acc.push(entry);
      return acc;
    }

    const isSameEnduser = previousObservation.enduserId === entry.enduserId;
    const isWithinTimestampThreshold =
      previousObservation.timestamp && entry.timestamp
        ? Math.abs(previousObservation.timestamp.getTime() - entry.timestamp.getTime()) <= 5000
        : false;
    const isDuplicateUnit = previousObservation.measurements.some(({ unit }) => unit === measurement.unit);

    if (!isSameEnduser || !isWithinTimestampThreshold || isDuplicateUnit) {
      acc.push(previousObservation);
      acc.push(entry);
      return acc;
    }

    const areBothAlwaysCombinedUnits =
      UNITS_ALWAYS_COMBINED.includes(measurement.unit) &&
      previousObservation.measurements.some((m) => UNITS_ALWAYS_COMBINED.includes(m.unit));
    const isPreviousLinkedToCurrentUnit = previousObservation.showWithPlotsByUnit?.includes(measurement.unit) ?? false;
    const isCurrentLinkedToPreviousUnit = previousObservation.measurements.some(
      (m) => entry.showWithPlotsByUnit?.includes(m.unit)
    );
    const hasUnitPairing = areBothAlwaysCombinedUnits || isPreviousLinkedToCurrentUnit || isCurrentLinkedToPreviousUnit;

    if (!hasUnitPairing) {
      acc.push(previousObservation);
      acc.push(entry);
      return acc;
    }

    const irregularHeartbeat = previousObservation.irregularHeartbeat || entry.irregularHeartbeat;
    const beforeMeal = previousObservation.beforeMeal || entry.beforeMeal;
    const reviewedAt = previousObservation.reviewedAt || entry.reviewedAt;
    const reviewedBy = previousObservation.reviewedBy || entry.reviewedBy;
    const status = previousObservation.status === "entered-in-error" ? previousObservation.status : entry.status;

    acc.push({
      ...previousObservation,
      measurements: [...previousObservation.measurements, ...entry.measurements],
      classifications: [...previousObservation.classifications, ...entry.classifications],
      showWithPlotsByUnit: Array.from(
        new Set([...(previousObservation.showWithPlotsByUnit ?? []), ...(entry.showWithPlotsByUnit ?? [])])
      ),
      irregularHeartbeat,
      beforeMeal,
      reviewedAt,
      reviewedBy,
      status,
    });
    return acc;
  }, []);
};

export const useVitalsQueryWithFilters = <TError = Error, TData = CombinedObservationList>(
  args?: UseVitalsQueryWithFiltersArgs,
  options?: UseVitalsQueryOptions<TError, TData>
) => {
  const { filters = {}, combineRelatedObservations: shouldCombine = true, limit = DEFAULT_VITALS_LIMIT } = args ?? {};
  const { select, ...restOptions } = options ?? {};

  const useCareTeamFilter = !!filters.careTeamIds?.length;

  // Classification filtering is client-side only when careTeamIds forces use of `filter`
  const needsClientSideClassificationFilter = useCareTeamFilter && !!filters.classifications?.values.length;

  // configurationIds filtering is client-side only when careTeamIds forces use of `filter`
  const needsClientSideConfigurationIdsFilter = useCareTeamFilter && !!filters.configurationIds?.length;

  const request = useMemo<ParamsForMethod<"enduser_observations", "getSome">>(() => {
    const base: ParamsForMethod<"enduser_observations", "getSome"> = {
      limit,
      sortBy: "timestamp",
      sort: "newFirst",
    };

    if (useCareTeamFilter) {
      base.filter = buildFilter(filters);
    } else {
      base.mdbFilter = buildMdbFilter(filters);
    }

    return base;
  }, [filters, limit, useCareTeamFilter]);

  const classificationPredicate = useMemo(
    () => (needsClientSideClassificationFilter ? buildClassificationPredicate(filters.classifications) : null),
    [needsClientSideClassificationFilter, filters.classifications]
  );

  return useTellescopeInfiniteQuery({
    ...restOptions,
    model: "enduser_observations",
    method: "getSome",
    request,
    select: (data) => {
      let flatObservations = data.pages.flat();

      // Step 1a: Client-side classification filtering (only when server-side wasn't possible)
      if (classificationPredicate) {
        flatObservations = flatObservations.filter(classificationPredicate);
      }

      // Step 1b: Client-side configurationIds filtering (only when server-side wasn't possible)
      if (needsClientSideConfigurationIdsFilter && filters.configurationIds) {
        const configIds = new Set(filters.configurationIds);
        flatObservations = flatObservations.filter(
          (obs) => obs.classifications?.some((c) => configIds.has(c.configurationId))
        );
      }

      // Step 2: Map to CombinedObservation shape
      const mapped: CombinedObservation[] = flatObservations.map(({ measurement, classifications = [], ...rest }) => ({
        ...rest,
        measurements: [{ ...measurement, observationId: rest.id }],
        classifications: classifications.map((c) => ({ ...c, observationId: rest.id })),
      }));

      // Step 3: Optionally combine related observations
      const result = shouldCombine ? combineRelatedObservations(mapped) : mapped;

      // Step 4: Apply caller's select if provided
      return select ? select(result) : result;
    },
  });
};
